import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { FC, useState } from "react"
import { getI18n, useTranslation } from "react-i18next"
import { MdLanguage } from "react-icons/md"
import { Tooltip } from "../../Tooltip"
import { usePageLayoutContext } from "../layoutContext"

const LanguageDropdown = ({
	isExpanded,
	languages,
	selectedLanguage,
	changeLanguage,
	translationFunc,
}: {
	isExpanded: boolean
	languages: { name: string; code: string }[]
	selectedLanguage: string
	changeLanguage: (lng: string) => void
	translationFunc: ReturnType<typeof useTranslation>["t"] | undefined
}) => {
	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger
				className={`${
					isExpanded === false ? `w-15` : `w-45`
				} focus:ring-dipai-primary-501/40 text-white/60 transition-all duration-300 ring-inset hover:text-white/100 focus:ring-[0px] focus:outline-hidden`}
			>
				<div
					tabIndex={-1}
					className={`group flex ${
						isExpanded === false ? `w-15` : `w-45`
					} flex-row`}
				>
					<Tooltip
						className={`${isExpanded === true ? `hidden` : `block`}`}
						sideOffset={24}
						side={"right"}
						customContent={
							translationFunc !== undefined ? translationFunc("LANGUAGE") : "LANGUAGE"
						}
					>
						<div
							tabIndex={-1}
							className={`flex h-13 items-center pr-3 pl-3.5`}
						>
							<MdLanguage className={`h-6 w-6`} />
						</div>
					</Tooltip>
					<div
						tabIndex={-1}
						className={`h-13 w-32.5 origin-left ${
							isExpanded === false ? `scale-x-0` : `scale-x-100`
						} duration-300`}
					>
						<div className={`flex h-full items-center overflow-hidden`}>
							<p
								className={`text-button w-27.5 text-left leading-[16px] font-semibold duration-300 group-hover:text-white/100 ${isExpanded === false ? `text-white/0` : `text-white/60`}`}
							>
								{`${
									languages.find((lang) => lang.code === selectedLanguage)
										?.name ?? "unknown language"
								}`}
							</p>
						</div>
					</div>
				</div>
			</DropdownMenu.Trigger>
			<DropdownMenu.Content
				onCloseAutoFocus={(e) => e.preventDefault()}
				side={"right"}
				sideOffset={-10}
				align={"end"}
				alignOffset={10}
				className="border-dipai-secondary-901 bg-dipai-secondary-800 shadow-popup z-1000 mt-2 w-50 rounded-sm border-1 p-1.5 text-white/60"
			>
				{languages.map((lang) => (
					<DropdownMenu.Item
						key={`languageSelector_${lang.name}`}
						className={`${
							selectedLanguage === lang.code
								? `text-menu text-dipai-primary-501 px-3.5 py-1`
								: `text-menu focus:ring-dipai-primary-501/40 cursor-pointer rounded-sm px-3.5 py-1 ring-inset hover:bg-white/20 hover:text-white/100 hover:outline-hidden focus:ring-[0px] focus:outline-hidden`
						}`}
						onSelect={(e) => {
							e.preventDefault()
							changeLanguage(lang.code)
						}}
					>
						<p>{lang.name}</p>
					</DropdownMenu.Item>
				))}
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	)
}

export interface ILanguageSelectorProps {
	i18n: ReturnType<typeof getI18n> & {}
	languages: { name: string; code: string }[]
}
export const MainNavLanguageSelector: FC<ILanguageSelectorProps> = (
	props: ILanguageSelectorProps
) => {
	const { i18n, languages } = props
	const { isMainNavExpanded, isMainNavPopupExpanded, translationFunc } = usePageLayoutContext()
	const [selectedLanguage, setSelectedLanguage] = useState(i18n.language)
	const changeLanguage = (lng: string) => {
		setSelectedLanguage(lng)
		i18n.changeLanguage(lng)
	}

	return (
		<>
			<div className={`hidden lg:flex`}>
				<LanguageDropdown
					changeLanguage={changeLanguage}
					languages={languages}
					selectedLanguage={selectedLanguage}
					isExpanded={isMainNavExpanded}
					translationFunc={translationFunc}
				/>
			</div>
			<div className={`flex sm:flex lg:hidden`}>
				<LanguageDropdown
					changeLanguage={changeLanguage}
					languages={languages}
					selectedLanguage={selectedLanguage}
					isExpanded={isMainNavPopupExpanded}
					translationFunc={translationFunc}
				/>
			</div>
		</>
	)
}
